import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '../Navbar';
import Header from '../Header';
import baseUrl from '../BaseUrl';
import Loader from '../Loader';
import RegistrationLanguage from './RegistrationLanguage';
import RegistrationDetails from './RegistrationDetails';
import TNManagement from './TNManagement';

const RegistrationSetup = () => {
    const [name,   setName]   = useState('');
    const [token,  setToken]  = useState('');
    const [expire, setExpire] = useState('');
    const [role,   setRole]   = useState('');
    const [access, setAccess] = useState([]);
    const [user,   setUser]   = useState(null);
    const [activeTab, setActiveTab] = useState('language');
    const [companies, setCompanies] = useState([]);
    const [allEmails, setAllEmails] = useState([]);
    const navigate  = useNavigate();
    const location  = useLocation();
    const [selectedCid, setSelectedCid] = useState(location.state?.cid || '');

    useEffect(() => { refreshToken(); getUserAccess(); }, []);
    useEffect(() => { if (role > 2) navigate('/dashboard'); }, [role]);
    useEffect(() => {
        if (user !== null && role !== '' && parseInt(role) > 1 && !user.hasEdit && !user.hasBase) navigate('/dashboard');
    }, [user, role]);
    useEffect(() => { if (token && user !== null) { loadCompanies(); loadAllEmails(); } }, [token, user]);

    const refreshToken = async () => {
        try {
            const res = await axios.get(`${baseUrl}/token`);
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setRole(decoded.role); setExpire(decoded.exp);
        } catch { navigate('/'); window.location.reload(); }
    };

    const getUserAccess = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/user-access`, { headers: { Authorization: `Bearer ${token}` } });
            setAccess(res.data.results);
            setUser({
                hasEdit: !!res.data.enrms_registration_edit_switch,
                hasBase: !!res.data.enrms_registration_switch
            });
        } catch (e) { navigate('/dashboard'); }
    };

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        if (expire * 1000 < Date.now()) {
            const res = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${res.data.accessToken}`;
            setToken(res.data.accessToken);
            const decoded = jwt_decode(res.data.accessToken);
            setName(decoded.name); setExpire(decoded.exp);
        }
        return config;
    }, err => Promise.reject(err));

    const loadCompanies = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/setup/company-list`, { headers: { Authorization: `Bearer ${token}` } });
            setCompanies(res.data.data);
        } catch (e) { console.log(e); }
    };

    const loadAllEmails = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/registration/admin/email-addresses/all`, { headers: { Authorization: `Bearer ${token}` } });
            setAllEmails(res.data.data);
        } catch (e) { console.log(e); }
    };

    const isSuperAdmin = role !== '' && role <= 1;
    const hasEdit = isSuperAdmin || !!user?.hasEdit;

    const sharedProps = { token, expire, setToken, setExpire, name, setName, selectedCid };

    const tabs = [
        { key: 'language', label: 'Manage Registration Language' },
        { key: 'details',  label: 'Manage Registration Details' },
        { key: 'tn',       label: 'Manage Registration for TN' }
    ];

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {user === null && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-6"><div className="left-title"><h1>Registration Setup</h1></div></div>
                        <div className="col-6 inventory-btn">
                            <div className="addnew-button" style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                                {activeTab === 'tn' && selectedCid && hasEdit && (
                                    <Link to="/enrms-registration-import" state={{ cid: selectedCid }} className="btn btn-outline-secondary btn-sm">Import TN / Tag File</Link>
                                )}
                                <Link to="/enrms-registration-export" state={{ cid: selectedCid }} className="btn btn-outline-secondary btn-sm">Export</Link>
                                <Link to="/enrms-registration"><span>&larr;</span> Back</Link>
                            </div>
                        </div>
                    </div>

                    {/* Customer Selector */}
                    <div className="row" style={{ marginBottom:'20px' }}>
                        <div className="col-md-4">
                            <label><strong>Customer</strong></label>
                            <select
                                className="form-control"
                                value={selectedCid}
                                onChange={e => setSelectedCid(e.target.value)}
                            >
                                <option value="">— Select Customer —</option>
                                {companies.map(c => <option key={c.id} value={c.id}>{c.cname}</option>)}
                            </select>
                        </div>
                    </div>

                    {!selectedCid && (
                        <div className="row"><div className="col-12"><p><em>Please select a customer to continue.</em></p></div></div>
                    )}

                    {selectedCid && hasEdit && (
                        <>
                            {/* Tab Nav */}
                            <div style={{ display:'flex', borderBottom:'2px solid #dee2e6', marginBottom:'20px' }}>
                                {tabs.map(t => (
                                    <button
                                        key={t.key}
                                        onClick={() => setActiveTab(t.key)}
                                        style={{
                                            padding:'8px 16px', border:'none', background:'none', cursor:'pointer',
                                            borderBottom: activeTab === t.key ? '2px solid #0d6efd' : '2px solid transparent',
                                            marginBottom:'-2px',
                                            color: activeTab === t.key ? '#0d6efd' : '#555',
                                            fontWeight: activeTab === t.key ? '600' : 'normal'
                                        }}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {activeTab === 'language' && <RegistrationLanguage {...sharedProps} />}
                            {activeTab === 'details'  && <RegistrationDetails  {...sharedProps} allEmails={allEmails} />}
                            {activeTab === 'tn'       && <TNManagement {...sharedProps} />}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegistrationSetup;
